import { Box, Divider, Flex, useBreakpoint, useDisclosure } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { MenuItemProps } from '@televet/kibble-ui/build/components/Menu/components/MenuItem';
import { Popover } from '@televet/kibble-ui/build/components/Popover';
import { Swatch } from '@televet/kibble-ui/build/components/Swatch';
import { Switch } from '@televet/kibble-ui/build/components/Switch';
import { TagProps } from '@televet/kibble-ui/build/components/Tag';
import React, { useCallback, useMemo } from 'react';
import FilterIconButton from 'shared/components/FilterIconButton/FilterIconButton';
import { useTeletriageClinicData } from '../hooks/useTeletriageClinicData';
import { TeletriageFilterProps } from '../hooks/useTeletriageConversationsList';
import { parseStatusGroupLabel } from '../utils/teletriageConversationStatus';
import { TeletriageConversationCenterFilterRow } from './TeletriageConversationCenterFilterRow';

enum TeletriageFilter {
  Status = 'status',
  Clinic = 'clinic',
}

export const TeletriageConversationCenterFilters = ({
  filters,
  setFilters,
  clearFilters,
  toggleShowArchived,
}: TeletriageFilterProps): JSX.Element => {
  const { isOpen, onToggle } = useDisclosure();
  const { teletriageStatuses, careClinics } = useTeletriageClinicData();
  const currentBreakpoint = useBreakpoint();

  const teletriageStatusesItems: MenuItemProps[] = useMemo(() => {
    return (
      teletriageStatuses.map((status) => ({
        label: status.name,
        leftElement: <Swatch bgColor={status.color} size="sm" />,
        value: status.name,
        groupLabel: parseStatusGroupLabel(status),
        isSelected: filters.conversationStatus.includes(status.name),
      })) || []
    );
  }, [filters.conversationStatus, teletriageStatuses]);

  const handleStatusSelection = useCallback(
    (selectedItems?: MenuItemProps[]): void => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        conversationStatus: selectedItems?.map((item) => item.value || '') || [],
      }));
    },
    [setFilters],
  );

  const careClinicsItems: MenuItemProps[] = useMemo(() => {
    return (
      careClinics.map((clinic) => ({
        value: clinic.id,
        label: clinic.name,
        groupLabel: 'Clinics',
        isSelected: filters.clinicIds.includes(clinic.id),
      })) || []
    );
  }, [careClinics, filters.clinicIds]);

  const handleClinicSelection = useCallback(
    (selectedItems?: MenuItemProps[]): void => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        clinicIds: selectedItems?.map((item) => item.value || '') || [],
      }));
    },
    [setFilters],
  );

  const handleTagClose = useCallback(
    ({ filter, id }: { filter: TeletriageFilter; id: string }): void => {
      if (filter === TeletriageFilter.Clinic) {
        setFilters((prevFilters) => ({
          ...prevFilters,
          clinicIds: filters.clinicIds.filter((clinicId) => clinicId !== id),
        }));
      }
      if (filter === TeletriageFilter.Status) {
        setFilters((prevFilters) => ({
          ...prevFilters,
          conversationStatus: filters.conversationStatus.filter((status) => status !== id),
        }));
      }
    },
    [filters, setFilters],
  );

  const selectedStatuses: TagProps[] = useMemo(() => {
    return filters.conversationStatus.map((status) => {
      const statusColor = teletriageStatuses.find((ts) => ts.name === status);
      return {
        label: status,
        leftElement: <Swatch bgColor={statusColor?.color} size="xs" w={3} h={3} />,
        onClose: (): void => {
          handleTagClose({ filter: TeletriageFilter.Status, id: status });
        },
      };
    });
  }, [filters.conversationStatus, handleTagClose, teletriageStatuses]);

  const selectedClinics: TagProps[] = useMemo(() => {
    return filters.clinicIds.map((clinicId) => {
      const clinicName = careClinics.find((cc) => cc.id === clinicId);
      return {
        label: clinicName?.name || '',
        onClose: (): void => {
          handleTagClose({ filter: TeletriageFilter.Clinic, id: clinicId });
        },
      };
    });
  }, [careClinics, filters.clinicIds, handleTagClose]);

  return (
    <Box className="TeletriageConversationCenter__Filters">
      <Popover
        isOpen={isOpen}
        anchor={
          <FilterIconButton
            showBadge={!!filters.clinicIds.length || !!filters.conversationStatus.length}
            onFilterToggle={onToggle}
          />
        }
        onClose={onToggle}
        size={currentBreakpoint === 'base' ? 'lg' : 'xl'}
        variant="default"
        placement="bottom-start"
        footerProps={{
          children: (
            <Flex alignItems="center" justifyContent="space-between">
              <Switch
                isFieldInline={true}
                label="Show Archived"
                labelPosition="end"
                isChecked={filters.showArchived}
                onChange={toggleShowArchived}
                size="sm"
              />
              <Button size="sm" onClick={onToggle}>
                Done
              </Button>
            </Flex>
          ),
          hideButtons: true,
        }}
      >
        <Flex alignItems="center" justifyContent="space-between">
          <Heading size="md">Filters</Heading>
          <Button variant="ghostNeutral" iconName="arrowCircle" size="sm" onClick={clearFilters}>
            Clear
          </Button>
        </Flex>
        <Box mx={-3} mb={-3}>
          <Divider borderColor="border.default" my={3} />
          <TeletriageConversationCenterFilterRow
            label="Status"
            selectedItems={selectedStatuses}
            menuItems={teletriageStatusesItems}
            onSelect={(_, allSelectedItems): void => handleStatusSelection(allSelectedItems)}
            onSelectAll={(allSelectedItems): void => handleStatusSelection(allSelectedItems)}
          />
          <Divider borderColor="border.default" my={3} />
          <TeletriageConversationCenterFilterRow
            label="Clinic"
            menuItems={careClinicsItems}
            onSelect={(_, allSelectedItems): void => handleClinicSelection(allSelectedItems)}
            onSelectAll={(allSelectedItems): void => handleClinicSelection(allSelectedItems)}
            selectedItems={selectedClinics}
          />
          <Divider borderColor="border.default" my={3} />
        </Box>
      </Popover>
    </Box>
  );
};
